import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDatabase,
    faRocket,
    faSpinner,
    faBolt,
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
    faClock,
    faRefresh,
    faServer,
    faTrash,
    faStar,
    faEye,
    faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import apiHelper from '../../utils/apiHelper';
import QdrantLogsViewer from './QdrantLogsViewer';
import CollectionDocumentsViewer from './CollectionDocumentsViewer';
import './IndexingManagement.css';

const IndexingManagement = () => {
    const [collections, setCollections] = useState([]);
    const [isLoadingCollections, setIsLoadingCollections] = useState(false);
    const [loadingCollectionNames, setLoadingCollectionNames] = useState([]);
    const [lastRebuildTime, setLastRebuildTime] = useState(null);
    const [qdrantStatus, setQdrantStatus] = useState('unknown');
    const [mainCollection, setMainCollection] = useState(null);
    const [isUpdatingActiveCollection, setIsUpdatingActiveCollection] = useState(false);

    // Complete Content Rebuild state
    const [completeSystemRebuilding, setCompleteSystemRebuilding] = useState(false);
    const [completeTestMode, setCompleteTestMode] = useState(false);
    const [completeTestLimit, setCompleteTestLimit] = useState(10);

    // Incremental Index state
    const [incrementalIndexing, setIncrementalIndexing] = useState(false);

    // Cleanup old collections state
    const [cleaningUpCollections, setCleaningUpCollections] = useState(false);

    // Logs viewer state
    const [showLogsViewer, setShowLogsViewer] = useState(false);
    const [logsAutoRefresh, setLogsAutoRefresh] = useState(false);
    
    // Collection documents viewer state
    const [showDocumentsViewer, setShowDocumentsViewer] = useState(false);
    const [selectedCollection, setSelectedCollection] = useState(null);
    
    // Debug document ID state
    const [debugDocumentId, setDebugDocumentId] = useState('4043');
    
    // WG Updates debug state
    const [showWGDebugModal, setShowWGDebugModal] = useState(false);
    const [wgDebugResults, setWGDebugResults] = useState(null);
    const [isLoadingWGDebug, setIsLoadingWGDebug] = useState(false);
    
    // WG Documents debug state
    const [showWGDocsModal, setShowWGDocsModal] = useState(false);
    const [wgDocsResults, setWGDocsResults] = useState(null);
    const [isLoadingWGDocs, setIsLoadingWGDocs] = useState(false);



    // Load collections on component mount
    useEffect(() => {
        loadCollections();
        checkQdrantStatus();
    }, []);

    const checkQdrantStatus = async () => {
        try {
            const response = await apiHelper.get('/api/qdrant/status');
            setQdrantStatus('online');
        } catch (error) {
            console.error('Qdrant status check failed:', error);
            setQdrantStatus('offline');
        }
    };

    const loadCollections = async () => {
        if (isLoadingCollections) {
            return;
        }
        
        setIsLoadingCollections(true);
        try {
            // First, quickly get collection names to show skeleton cards
            const namesResponse = await apiHelper.get('/api/qdrant/collections/names');
            const allCollections = namesResponse.data.collections || [];
            const activeCollection = namesResponse.data.mainCollection || null;
            
            // Filter to only show irish-funds related collections
            const basicCollections = allCollections.filter(collection => 
                collection.name.toLowerCase().includes('irish')
            );
            
            // Set main collection immediately
            setMainCollection(activeCollection);
            
            // Set basic collections with explicit loading state
            const collectionsWithLoadingState = basicCollections.map(c => ({
                ...c,
                isLoading: true
            }));
            setCollections(collectionsWithLoadingState);
            setLoadingCollectionNames(basicCollections.map(c => c.name));
            
            // Infer last rebuild time from collection timestamps
            const inferLastRebuildTime = () => {
                
                let mostRecentTimestamp = null;
                
                basicCollections.forEach(collection => {
                    
                    
                    // Look for Unix timestamp pattern (like "temp_irish_funds_1751987545573")
                    const unixTimestampMatch = collection.name.match(/(\d{13})/); // 13 digits for milliseconds
                    if (unixTimestampMatch) {
                        
                        try {
                            const timestamp = parseInt(unixTimestampMatch[1]);
                            const date = new Date(timestamp);
                            
                            
                            if (!mostRecentTimestamp || date > mostRecentTimestamp) {
                                mostRecentTimestamp = date;
                                
                            }
                        } catch (error) {
                            console.warn('Could not parse Unix timestamp from collection name:', collection.name, error);
                        }
                    } else {
                        // Also try the date format pattern for future use (like "irish_funds_2025-07-11-17-53-27")
                        const dateTimestampMatch = collection.name.match(/(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})/);
                        if (dateTimestampMatch) {
                            
                            try {
                                const timestamp = dateTimestampMatch[1];
                                const [year, month, day, hour, minute, second] = timestamp.split('-');
                                const date = new Date(year, month - 1, day, hour, minute, second);
                                
                                
                                if (!mostRecentTimestamp || date > mostRecentTimestamp) {
                                    mostRecentTimestamp = date;
                                    
                                }
                            } catch (error) {
                                console.warn('Could not parse date timestamp from collection name:', collection.name, error);
                            }
                        } 
                    }
                });
                
                if (mostRecentTimestamp) {
                    setLastRebuildTime(mostRecentTimestamp);
                } 
            };
            
            // Only infer if we don't already have a lastRebuildTime
            if (!lastRebuildTime) {
                inferLastRebuildTime();
            }
            
            // Now load detailed information progressively
            const loadCollectionDetails = async (collectionName) => {
                try {
                    // Create a timeout promise (30 seconds)
                    const timeoutPromise = new Promise((_, reject) => 
                        setTimeout(() => {
                            reject(new Error('Request timeout after 30 seconds'));
                        }, 30000)
                    );
                    
                    // Race between the API call and timeout
                    const detailsResponse = await Promise.race([
                        apiHelper.get(`/api/qdrant/collections/${collectionName}/details`),
                        timeoutPromise
                    ]);
                    
                    const collectionDetails = detailsResponse.data;
                    
                    // Update this specific collection with detailed info - explicitly set isLoading to false
                    setCollections(prevCollections => 
                        prevCollections.map(collection => 
                            collection.name === collectionName 
                                ? { ...collectionDetails, isLoading: false, name: collectionName }
                                : collection
                        )
                    );
                    
                    // Remove from loading list
                    setLoadingCollectionNames(prevLoading => 
                        prevLoading.filter(name => name !== collectionName)
                    );
                } catch (error) {
                    console.error(`Error loading details for ${collectionName}:`, error);
                    const errorMessage = error.message?.includes('timeout') 
                        ? 'Loading timeout - collection may be very large'
                        : error.message;
                    
                    // Update collection with error state
                    setCollections(prevCollections => 
                        prevCollections.map(collection => 
                            collection.name === collectionName 
                                ? { 
                                    ...collection, 
                                    isLoading: false, 
                                    status: 'error',
                                    error: errorMessage
                                }
                                : collection
                        )
                    );
                    
                    // Remove from loading list
                    setLoadingCollectionNames(prevLoading => 
                        prevLoading.filter(name => name !== collectionName)
                    );
                }
            };
            
            // Load details for each collection sequentially to avoid overwhelming Qdrant
            // When all 4 collections request 50k points simultaneously, it times out
            const loadDetailsSequentially = async () => {
                for (const collection of basicCollections) {
                    await loadCollectionDetails(collection.name);
                    // Small delay between requests to prevent overwhelming the backend
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            };
            loadDetailsSequentially();
            
        } catch (error) {
            console.error('Error loading collections:', error);
            toast.error('Failed to load collections');
            setCollections([]);
            setMainCollection(null);
        } finally {
            setIsLoadingCollections(false);
        }
    };







    const startCompleteSystemRebuild = async () => {
        const confirmMessage = `COMPLETE CONTENT REBUILD

This will:
✅ Create a new temporary collection
✅ Index ALL news articles (weekly + submitted)
✅ Index ALL repository documents (all years)
📦 Ready for future content sources
🔧 Manual promotion when you're ready
🛡️ All collections preserved (no auto-deletion)

${completeTestMode ? `🧪 TEST MODE: ${completeTestLimit} items only` : '⚡ FULL REBUILD: All content'}

📝 Note: This creates a TEMP collection - you'll manage promotion/cleanup manually later.

The process may take 15-30 minutes. Continue?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setCompleteSystemRebuilding(true);

        try {
            // Fire and forget - don't wait for response (takes 3 hours!)
            // Backend will continue processing and log to SharePoint
            apiHelper.post('/api/qdrant/rebuild-complete-content', {
                sessionId: 'complete-content-rebuild-' + Date.now(),
                testLimit: completeTestMode ? completeTestLimit : null,
                repositoryYear: 'all' // Always index all years for complete rebuild
            }).catch(err => {
                // Ignore timeout errors - backend is still working!
                console.log('Request sent successfully, backend is processing...');
            });

            // Immediately show success message and tell user to check logs
            const startMessage = `🚀 Complete Content Rebuild Started!

⏱️ Estimated Time: ${completeTestMode ? '5-10 minutes' : '~3 hours'}
📊 Content Sources: Repository + Events + News + Working Groups + WG Updates

✅ Process is running in the background
👁️ Logs viewer will open automatically in 1 second!
🔄 Refresh collections list when complete

${completeTestMode
  ? `🧪 TEST MODE: Only ${completeTestLimit} items per section will be indexed into a NEW temp collection. Active collection will NOT be replaced. Inspect the temp collection in admin centre, then delete it manually when done.`
  : '⚡ Full rebuild in progress — active collection will be replaced when complete.'}
`;

            toast.success(startMessage, { autoClose: 8000 });
            
            // 🔥 AUTO-OPEN LOGS VIEWER to watch progress in real-time
            setTimeout(() => {
                setLogsAutoRefresh(true); // Enable auto-refresh
                setShowLogsViewer(true);
            }, 1000); // Open after 1 second so toast is visible first
            
            // Update last rebuild time
            setLastRebuildTime(new Date());

        } catch (error) {
            console.error('Failed to start rebuild:', error);
            toast.error(`Failed to start rebuild: ${error.message}`);
        } finally {
            // Clear rebuilding state immediately
            setCompleteSystemRebuilding(false);
        }
    };



    const startIncrementalIndex = async () => {
        if (!window.confirm('Run incremental index now?\n\nThis will re-index only documents modified since the last sync (minus 6h overlap). Should take a few minutes.')) {
            return;
        }

        setIncrementalIndexing(true);

        try {
            apiHelper.post('/api/qdrant/incremental-index', {
                sessionId: 'incremental-' + Date.now(),
            }).catch(() => {
                console.log('Incremental index request sent, backend is processing...');
            });

            toast.success('⚡ Incremental index started!\n\nOnly modified documents will be re-indexed. Check the logs viewer for progress.', { autoClose: 6000 });

            setTimeout(() => {
                setLogsAutoRefresh(true);
                setShowLogsViewer(true);
            }, 1000);

        } catch (error) {
            console.error('Failed to start incremental index:', error);
            toast.error(`Failed to start incremental index: ${error.message}`);
        } finally {
            setIncrementalIndexing(false);
        }
    };

    const cleanupOldCollections = async () => {
        if (!window.confirm('Clean up old collections?\n\nThis will keep the active collection + the 2 most recent previous collections, and permanently delete all others.\n\nThis cannot be undone.')) {
            return;
        }

        setCleaningUpCollections(true);

        try {
            apiHelper.post('/api/qdrant/cleanup-old-collections', {
                sessionId: 'cleanup-' + Date.now(),
            }).catch(() => {
                console.log('Cleanup request sent, backend is processing...');
            });

            toast.success('🗑️ Collection cleanup started!\n\nActive + 2 most recent collections will be kept. Check logs for details.', { autoClose: 6000 });

            setTimeout(() => {
                setLogsAutoRefresh(true);
                setShowLogsViewer(true);
            }, 1000);

        } catch (error) {
            console.error('Failed to start collection cleanup:', error);
            toast.error(`Failed to start cleanup: ${error.message}`);
        } finally {
            setCleaningUpCollections(false);
        }
    };

    // Helper function to convert timestamp to Irish format
    const formatTimestampIrish = (timestamp) => {
        try {
            // Parse timestamp like "2025-07-11-17-53-27" 
            const parts = timestamp.split('-');
            if (parts.length === 6) {
                const [year, month, day, hour, minute, second] = parts;
                return `${day}/${month}/${year} ${hour}:${minute}`;
            }
            return timestamp; // Return original if parsing fails
        } catch (error) {
            return timestamp; // Return original if parsing fails
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'online': return { icon: faCheckCircle, color: '#28a745', label: 'Online' };
            case 'offline': return { icon: faExclamationTriangle, color: '#dc3545', label: 'Offline' };
            default: return { icon: faInfoCircle, color: '#6c757d', label: 'Unknown' };
        }
    };

    const getCollectionIcon = (name) => {
        if (name === mainCollection) return faDatabase; // Active collection gets database icon
        return faServer; // All other collections get server icon
    };

    const formatCollectionName = (name) => {
        if (name === mainCollection) {
            // Clean up collection names when they become main
            if (name.startsWith('irish_funds_')) {
                const timestamp = name.replace('irish_funds_', '');
                return `⭐ Irish Funds Collection (${formatTimestampIrish(timestamp)})`;
            }
            if (name.includes('temp_irish_funds_')) {
                const timestamp = name.split('_').pop();
                return `⭐ Irish Funds Collection (${formatTimestampIrish(timestamp)})`;
            }
            return `⭐ ${name}`;
        }
        
        // Non-active collections - clean names without status labels
        if (name.startsWith('irish_funds_')) {
            const timestamp = name.replace('irish_funds_', '');
            return `Irish Funds Collection (${formatTimestampIrish(timestamp)})`;
        }
        
        return name;
    };

    const renderCollectionCard = (collection, index) => {
        const isLoading = collection.isLoading || loadingCollectionNames.includes(collection.name);
        
        return (
            <div 
                key={index} 
                className="collection-card"
            >
                <div className="collection-card-inner">
                    <div className="collection-header">
                        <div className="collection-left">
                            <FontAwesomeIcon
                                icon={getCollectionIcon(collection.name)}
                                className="collection-icon"
                            />
                            <div className="collection-info">
                                <h4>
                                    {formatCollectionName(collection.name)}
                                </h4>
                            </div>
                        </div>
                        <div className="collection-right">
                            <div className="collection-actions">
                                {collection.name !== mainCollection && !isLoading && (
                                    <button
                                        onClick={() => updateActiveCollection(collection.name)}
                                        className="btn btn-sm btn-primary"
                                        disabled={isUpdatingActiveCollection}
                                    >
                                        {isUpdatingActiveCollection ? 'Updating...' : 'Set as Main'}
                                    </button>
                                )}
                                {!isLoading && (
                                    <button
                                        onClick={() => deleteCollection(collection.name)}
                                        className="btn btn-sm btn-danger"
                                        disabled={collection.name === mainCollection}
                                        title={collection.name === mainCollection ? 'Cannot delete main collection' : 'Delete collection'}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Technical name */}
                    <div style={{ 
                        color: '#666', 
                        fontSize: '0.8rem', 
                        fontFamily: 'monospace',
                        marginBottom: '12px',
                        paddingLeft: '8px'
                    }}>
                        {collection.name}
                    </div>
                    
                    {isLoading ? (
                        <div className="collection-loading">
                            <div className="loading-spinner">
                                <FontAwesomeIcon icon={faSpinner} spin />
                            </div>
                            <div className="loading-skeleton">
                                <div className="skeleton-line"></div>
                                <div className="skeleton-line short"></div>
                                <div className="skeleton-line"></div>
                                <div className="skeleton-line short"></div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Enhanced Stats Grid */}
                            <div className="collection-stats-grid">
                                {/* Basic Stats Row */}
                                <div className="stats-row">
                                    <div className="stat-item">
                                        <span className="stat-label">Vectors:</span>
                                        <span className="stat-value">{collection.vectors?.size || collection.config?.params?.vectors?.size || 1536}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Distance:</span>
                                        <span className="stat-value">{collection.vectors?.distance || collection.config?.params?.vectors?.distance || 'Cosine'}</span>
                                    </div>
                                                                                <div className="stat-item">
                                                <span className="stat-label">Chunks:</span>
                                                <span className="stat-value points-count">{collection.points_count?.toLocaleString() || 0}</span>
                                            </div>
                                </div>
                                
                                {/* Content Stats Row */}
                                <div className="stats-row">
                                    <div className="stat-item">
                                        <span className="stat-label">📝 Words:</span>
                                        <span className="stat-value">{collection.estimated_words?.toLocaleString() || 'N/A'}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">🎯 Tokens:</span>
                                        <span className="stat-value">{collection.estimated_tokens?.toLocaleString() || 'N/A'}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">🏗️ Segments:</span>
                                        <span className="stat-value">{collection.segments || 0}</span>
                                    </div>
                                </div>
                                
                                {/* Storage and Status Row */}
                                <div className="stats-row">
                                    <div className="stat-item">
                                        <span className="stat-label">💾 Storage:</span>
                                        <span className="stat-value storage-size">{collection.storage_info?.size_formatted || 'Unknown'}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">⚡ Status:</span>
                                        <span className={`stat-value status-${collection.status || 'unknown'}`}>
                                            {collection.status === 'green' ? '🟢 Healthy' : 
                                             collection.status === 'yellow' ? '🟡 Warning' : 
                                             collection.status === 'red' ? '🔴 Error' : 
                                             collection.status === 'error' ? 'Error' :
                                             collection.status || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">🕐 Age:</span>
                                        <span className="stat-value">{collection.age || 'Unknown'}</span>
                                    </div>
                                </div>
                                
                                {/* Optimization Row */}
                                <div className="stats-row">
                                    <div className="stat-item">
                                        <span className="stat-label">⚙️ Optimization:</span>
                                        <span className={`stat-value optimization-${collection.optimization_status || 'unknown'}`}>
                                            {collection.optimization_status === 'optimized' ? '✅ Optimized' : 
                                             collection.optimization_status === 'needs_optimization' ? '⚠️ Needs Opt' : 
                                             '❓ Unknown'}
                                        </span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">📊 Avg Size:</span>
                                        <span className="stat-value">
                                            {collection.storage_info?.size_bytes && collection.points_count > 0 ? 
                                             `${((collection.storage_info.size_bytes / collection.points_count) / 1024).toFixed(1)} KB/doc` : 
                                             'N/A'}
                                        </span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">🔢 Efficiency:</span>
                                        <span className="stat-value">
                                            {collection.points_count > 0 ? `${(collection.points_count / (collection.segments || 1)).toFixed(0)} pts/seg` : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Points Density Bar */}
                                
                                
                                
                                {/* Content Breakdown */}
                                {collection.content_breakdown ? (
                                    <div className="content-breakdown">
                                        <h5>📊 Content Breakdown</h5>
                                        <div className="breakdown-note">
                                            <small style={{ color: '#6c757d', fontStyle: 'italic' }}>
                                                Unique documents (not content chunks)
                                            </small>
                                        </div>
                                        <div className="content-types">
                                            {Object.entries(collection.content_breakdown)
                                                .filter(([, count]) => count > 0)
                                                .map(([type, count]) => (
                                                    <div key={type} className={`content-type ${type.toLowerCase().replace(/\s+/g, '-')}`}>
                                                        <span className="type-label">
                                                            {type === 'SubmittedArticles' ? 'Submitted Articles' : 
                                                             type === 'WorkingGroupUpdate' ? 'WG Updates' : 
                                                             type}
                                                        </span>
                                                        <span className="type-count">{count.toLocaleString()}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        
                                        {/* Other Types Breakdown
                                        {collection.other_types_breakdown && Object.keys(collection.other_types_breakdown).length > 0 && (
                                            <div className="other-types-detail">
                                                <h6>⚠️ Other Types Detail</h6>
                                                <div className="other-types-list">
                                                    {Object.entries(collection.other_types_breakdown).map(([type, count]) => (
                                                        <div key={type} className="other-type-item">
                                                            <span className="other-type-name">"{type}"</span>
                                                            <span className="other-type-count">{count}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )} */}
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const updateActiveCollection = async (collectionName) => {
        const isMainCollection = collectionName === mainCollection;

        if (isMainCollection) {
            toast.info(`"${collectionName}" is already the active collection`);
            return;
        }

        const confirmMessage = `🔄 Change Active Collection

Current: "${mainCollection}"
New: "${collectionName}"

This will immediately switch the active collection used by:
• Search functionality
• AI assistant (Daire)
• Document indexing
• All API operations

The change takes effect immediately without server restart.

Are you sure you want to make "${collectionName}" the active collection?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setIsUpdatingActiveCollection(true);

        try {
            await apiHelper.put('/api/graph/settings/active-qdrant-collection', {
                collectionName: collectionName
            });

            toast.success(`✅ Active collection changed to "${collectionName}"`);
            await loadCollections(); // Refresh to update the main collection indicator

        } catch (error) {
            console.error('Error updating active collection:', error);
            toast.error(`Failed to update active collection: ${error.message}`);
        } finally {
            setIsUpdatingActiveCollection(false);
        }
    };

    const deleteCollection = async (collectionName) => {
        if (collectionName === mainCollection) {
            toast.error('Cannot delete the main collection. Please set a different collection as main first.');
            return;
        }

        const confirmDelete = window.confirm(
            `Are you sure you want to delete the collection "${collectionName}"?\n\nThis action cannot be undone.`
        );

        if (!confirmDelete) return;

        try {
            const response = await apiHelper.delete(`/api/qdrant/collections/${collectionName}`);
            if (response.data.success) {
                toast.success(`Collection "${collectionName}" deleted successfully`);
                loadCollections(); // Refresh the list
            } else {
                toast.error(response.data.error || 'Failed to delete collection');
            }
        } catch (error) {
            console.error('Error deleting collection:', error);
            toast.error('Error deleting collection');
        }
    };

    const viewCollectionDocuments = (collection) => {
        // Safety check - don't open modal if collection is still loading
        if (!collection || collection.isLoading || loadingCollectionNames.includes(collection.name)) {
           
            return;
        }
        
        // Safety check - ensure collection has required data
        if (!collection.name || collection.points_count === undefined) {
            toast.error('Collection data is incomplete. Please wait for loading to finish.');
            return;
        }
        

        setSelectedCollection(collection);
        setShowDocumentsViewer(true);
    };

    const closeDocumentsViewer = () => {
        setShowDocumentsViewer(false);
        setSelectedCollection(null);
    };

    const debugWGUpdates = async () => {
        setIsLoadingWGDebug(true);
        setShowWGDebugModal(true);
        setWGDebugResults(null);
        
        try {
            const response = await apiHelper.get('/api/qdrant/debug-wg-updates');
            setWGDebugResults(response.data);
        } catch (error) {
            console.error('Error debugging WG updates:', error);
            setWGDebugResults({
                success: false,
                error: error.message || 'Failed to fetch debug information'
            });
        } finally {
            setIsLoadingWGDebug(false);
        }
    };

    const debugWGDocuments = async () => {
        setIsLoadingWGDocs(true);
        setShowWGDocsModal(true);
        setWGDocsResults(null);
        
        try {
            const response = await apiHelper.get('/api/qdrant/debug-wg-documents');
            setWGDocsResults(response.data);
        } catch (error) {
            console.error('Error debugging WG documents:', error);
            setWGDocsResults({
                success: false,
                error: error.message || 'Failed to fetch debug information'
            });
        } finally {
            setIsLoadingWGDocs(false);
        }
    };


    const statusInfo = getStatusIcon(qdrantStatus);

    return (
        <div className="indexing-management">
            <div className="indexing-header">
                <div className="header-title">
                    <FontAwesomeIcon icon={faDatabase} className="header-icon" />
                    <div>
                        <h2>Indexing Management</h2>
                        <p>Manage Qdrant collections and indexing operations</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="refresh-btn"
                        onClick={loadCollections}
                        disabled={isLoadingCollections}
                        title="Refresh Collections"
                    >
                        <FontAwesomeIcon icon={faRefresh} spin={isLoadingCollections} />
                    </button>
                </div>
            </div>

            {/* Status Section */}
            <div className="status-section">
                <div className="status-card">
                    <div className="status-indicator">
                        <FontAwesomeIcon
                            icon={statusInfo.icon}
                            style={{ color: statusInfo.color }}
                        />
                    </div>
                    <div className="status-details">
                        <h3>Qdrant Status</h3>
                        <p style={{ color: statusInfo.color, fontWeight: 'bold' }}>
                            {statusInfo.label}
                        </p>
                    </div>
                </div>

                <div className="status-card">
                    <div className="status-indicator">
                        <FontAwesomeIcon icon={faDatabase} />
                    </div>
                    <div className="status-details">
                        <h3>Collections</h3>
                        <p>{collections.length} active</p>
                    </div>
                </div>

                <div className="status-card">
                    <div className="status-indicator">
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div className="status-details">
                        <h3>Last Rebuild</h3>
                        <p>{lastRebuildTime ? lastRebuildTime.toLocaleString() : 'Never'}</p>
                    </div>
                </div>
            </div>






            {/* Collections Section */}
            <div className="collections-section">
                <div className="section-header">
                    <h3>Active Collections</h3>
                    <p>Current Qdrant collections and their status</p>
                </div>

                {isLoadingCollections ? (
                    <div className="loading-state">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Loading collections...</span>
                    </div>
                ) : collections.length > 0 ? (
                    <div className="collections-grid">
                        {collections
                            .sort((a, b) => {
                                // Show main collection first
                                if (a.name === mainCollection) return -1;
                                if (b.name === mainCollection) return 1;
                                // Then sort by name alphabetically
                                return a.name.localeCompare(b.name);
                            })
                            .map((collection, index) => renderCollectionCard(collection, index)
                        )}
                    </div>
                ) : (
                    <div className="empty-state">
                        <FontAwesomeIcon icon={faDatabase} />
                        <p>No collections found</p>
                    </div>
                )}
            </div>

            
            {/* Complete Content Rebuild Section */}
            <div className="complete-system-rebuild-section">
                <div className="section-header">
                    <h3 style={{ color: '#495057' }}>
                        
                         Complete Content Rebuild
                    </h3>
                    <p style={{ color: '#6c757d', fontWeight: '500' }}>
                        ALL CONTENT SOURCES: Creates temp collection with repository (first) + events + news + working groups + future sources
                    </p>
                 
                </div>

                <p style={{ color: 'red', fontWeight: '500', marginBottom: '20px'  }}>
                        <strong>Note:</strong> There is already a flow running nightly to index data, only run this if this is failing or need an index now - note it takes approximately 3 hours to complete
                    </p>

                <div className="complete-system-controls" style={{ display: 'block', width: '100%' }}>
                    {/* Test Mode for Complete System */}
                    <div className="test-mode-controls">
                        <div className="test-mode-toggle">
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={completeTestMode}
                                    onChange={(e) => setCompleteTestMode(e.target.checked)}
                                    disabled={completeSystemRebuilding}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className="toggle-label">
                                🧪 Test Mode {completeTestMode ? 'ON' : 'OFF'}
                            </span>
                        </div>

                        {completeTestMode && (
                            <div className="test-limit-input">
                                <label>Items to process (all sources):</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={completeTestLimit}
                                    onChange={(e) => setCompleteTestLimit(parseInt(e.target.value) || 10)}
                                    disabled={completeSystemRebuilding}
                                />
                            </div>
                        )}
                    </div>

                    <button
                        className="complete-rebuild-btn"
                        onClick={startCompleteSystemRebuild}
                        disabled={completeSystemRebuilding || incrementalIndexing || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #6c757d, #495057)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            width: '100%',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            marginTop: '16px',
                            display: 'block',
                            textAlign: 'center'
                        }}
                    >
                        {completeSystemRebuilding ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}🔧 Rebuilding All Content...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faRocket} />
                                {' '}{completeTestMode
                                    ? `🧪 Test Content Rebuild (${completeTestLimit} items)`
                                    : `🔧 Start Complete Content Rebuild`
                                }
                            </>
                        )}
                    </button>

                    <button
                        onClick={startIncrementalIndex}
                        disabled={incrementalIndexing || completeSystemRebuilding || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #17a2b8, #138496)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            width: '100%',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            marginTop: '10px',
                            display: 'block',
                            textAlign: 'center',
                            cursor: incrementalIndexing || completeSystemRebuilding || qdrantStatus !== 'online' ? 'not-allowed' : 'pointer',
                            opacity: incrementalIndexing || completeSystemRebuilding || qdrantStatus !== 'online' ? 0.6 : 1,
                        }}
                    >
                        {incrementalIndexing ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}⚡ Starting Incremental Index...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faBolt} />
                                {' '}⚡ Start Incremental Index
                            </>
                        )}
                    </button>

                    <button
                        onClick={cleanupOldCollections}
                        disabled={cleaningUpCollections || completeSystemRebuilding || incrementalIndexing || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #fd7e14, #e8680a)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            width: '100%',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            marginTop: '10px',
                            display: 'block',
                            textAlign: 'center',
                            cursor: cleaningUpCollections || completeSystemRebuilding || incrementalIndexing || qdrantStatus !== 'online' ? 'not-allowed' : 'pointer',
                            opacity: cleaningUpCollections || completeSystemRebuilding || incrementalIndexing || qdrantStatus !== 'online' ? 0.6 : 1,
                        }}
                    >
                        {cleaningUpCollections ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}🗑️ Cleaning Up Collections...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrash} />
                                {' '}🗑️ Clean Up Old Collections
                            </>
                        )}
                    </button>
                </div>

                <div className="complete-system-info">
                    <div className="info-card">
                        <h4>🎯 What this does (in order):</h4>
                        <ul>
                            <li>✅ Creates temporary collection</li>
                            <li>🥇 Indexes ALL repository documents (FIRST)</li>
                            <li>✅ Indexes ALL events</li>
                            <li>✅ Indexes ALL news articles</li>
                            <li>✅ Indexes ALL working groups</li> 
                            <li>✅ Promotes to main collection (if no errors)</li>
                            <li>📦 Ready for future content sources!</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Logs Viewer Section */}
            <div className="logs-viewer-section">
                <div className="section-header">
                    <h3 style={{ color: '#3498DB' }}>
                        <FontAwesomeIcon icon={faEye} className="section-icon" />
                        📋 Processing Logs
                    </h3>
                    <p style={{ color: '#6c757d', fontWeight: '500' }}>
                        View historical and live processing logs from SharePoint
                    </p>
                </div>

                <div className="logs-viewer-controls" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                        className="logs-viewer-btn"
                        onClick={() => setShowLogsViewer(true)}
                        style={{
                            background: 'linear-gradient(135deg, #3498DB, #2980B9)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.3)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        <FontAwesomeIcon icon={faClipboardList} />
                        📊 View Processing Logs
                    </button>
                    
                    <button
                        className="wg-debug-btn"
                        onClick={debugWGUpdates}
                        disabled={isLoadingWGDebug}
                        style={{
                            background: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!isLoadingWGDebug) {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(155, 89, 182, 0.3)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        {isLoadingWGDebug ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Checking...</>
                        ) : (
                            <><FontAwesomeIcon icon={faInfoCircle} /> 🔍 Debug WG Updates</>
                        )}
                    </button>
                    
                    <button
                        className="wg-docs-debug-btn"
                        onClick={debugWGDocuments}
                        disabled={isLoadingWGDocs}
                        style={{
                            background: 'linear-gradient(135deg, #e67e22, #d35400)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!isLoadingWGDocs) {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(230, 126, 34, 0.3)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        {isLoadingWGDocs ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Checking...</>
                        ) : (
                            <><FontAwesomeIcon icon={faInfoCircle} /> 📄 Debug WG Documents</>
                        )}
                    </button>
                </div>

                <div className="logs-viewer-info">
                    <div className="info-card">
                        <h4>📋 What you'll see:</h4>
                        <ul>
                            <li>🔄 <strong>Live Updates:</strong> Real-time processing status during rebuilds</li>
                            <li>📊 <strong>Historical Data:</strong> Previous rebuild results and performance</li>
                            <li>🎯 <strong>Detailed Tracking:</strong> Progress per content source (news, repository, etc.)</li>
                            <li>🚨 <strong>Error Monitoring:</strong> Failed indexing attempts and error details</li>
                            <li>📈 <strong>Performance Metrics:</strong> Processing times and success rates</li>
                        </ul>
                    </div>
                </div>
            </div>

            
            {/* Repository Debug Section */}
            <div className="repository-debug-section" style={{ display: 'none' }}>
                <div className="section-header">
                    <h3 style={{ color: '#dc3545' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="section-icon" />
                        🔍 Repository Debug (Troubleshooting)
                    </h3>
                    <p style={{ color: '#6c757d', fontWeight: '500' }}>
                        Debug repository indexing issue - test different approaches and identify the problem
                    </p>
                </div>

                <div className="debug-controls" style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                    <button
                        className="debug-btn"
                        onClick={async () => {
                            try {
                                setCompleteSystemRebuilding(true);
                                const response = await apiHelper.post('/api/qdrant/debug-repository', {
                                    sessionId: 'debug-repository-' + Date.now(),
                                    testLimit: 5
                                });

                                if (response.data.success) {
                                    const debug = response.data.debug;
                                    let message = `🔍 Repository Debug Results:\n\n`;

                                    // Environment variables
                                    message += `📋 Environment Variables:\n`;
                                    Object.entries(debug.environmentVariables).forEach(([key, value]) => {
                                        message += `• ${key}: ${value ? '✅' : 'MISSING'}\n`;
                                    });

                                    message += `\n🧪 Approach Tests:\n`;

                                    // Approach 1
                                    const approach1 = debug.approaches.approach1;
                                    if (approach1) {
                                        message += `• ${approach1.name}: ${approach1.status}\n`;
                                        if (approach1.itemsFound !== undefined) {
                                            message += `  Items found: ${approach1.itemsFound}\n`;
                                        }
                                        if (approach1.error) {
                                            message += `  Error: ${approach1.error}\n`;
                                        }
                                    }

                                    // Approach 2
                                    const approach2 = debug.approaches.approach2;
                                    if (approach2) {
                                        message += `• ${approach2.name}: ${approach2.status}\n`;
                                        if (approach2.itemsFound !== undefined) {
                                            message += `  Items found: ${approach2.itemsFound}\n`;
                                        }
                                        if (approach2.error) {
                                            message += `  Error: ${approach2.error}\n`;
                                        }
                                    }

                                    // Recommendations
                                    if (debug.recommendations.length > 0) {
                                        message += `\n🔧 Recommendations:\n`;
                                        debug.recommendations.forEach((rec, index) => {
                                            message += `${index + 1}. ${rec.action}\n   ${rec.description}\n`;
                                        });
                                    }

                                    toast.success(message, { autoClose: 15000 });
                                } else {
                                    throw new Error(response.data.error || 'Debug failed');
                                }
                            } catch (error) {
                                console.error('Debug repository error:', error);
                                toast.error(`Debug failed: ${error.message}`);
                            } finally {
                                setCompleteSystemRebuilding(false);
                            }
                        }}
                        disabled={completeSystemRebuilding || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #dc3545, #c82333)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '10px 16px',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {completeSystemRebuilding ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}🔍 Debugging...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {' '}🔍 Debug Repository Access
                            </>
                        )}
                    </button>

                    <button
                        className="test-repository-btn"
                        onClick={async () => {
                            try {
                                setCompleteSystemRebuilding(true);
                                const response = await apiHelper.post('/api/qdrant/test-repository-indexing', {
                                    sessionId: 'test-repository-' + Date.now(),
                                    testLimit: 5
                                });

                                if (response.data.success) {
                                    const successMessage = `🧪 Repository Test Results:

✅ Approach: ${response.data.approach}
📊 Documents indexed: ${response.data.documentsIndexed}
Errors: ${response.data.errors}
🗂️ Collection: ${response.data.collection}

${response.data.successfulIndexes.length > 0 ?
                                            `✅ Successfully indexed:
${response.data.successfulIndexes.map(doc => `• ${doc.title} (${doc.year})`).join('\n')}` : ''}

${response.data.failedIndexes.length > 0 ?
                                            `Failed to index:
${response.data.failedIndexes.map(doc => `• ${doc.title}: ${doc.error}`).join('\n')}` : ''}`;

                                    toast.success(successMessage, { autoClose: 10000 });
                                } else {
                                    throw new Error(response.data.error || 'Test failed');
                                }
                            } catch (error) {
                                console.error('Test repository error:', error);
                                toast.error(`Repository test failed: ${error.message}`);
                            } finally {
                                setCompleteSystemRebuilding(false);
                            }
                        }}
                        disabled={completeSystemRebuilding || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #28a745, #218838)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '10px 16px',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {completeSystemRebuilding ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}🧪 Testing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faDatabase} />
                                {' '}🧪 Test Repository Indexing
                            </>
                        )}
                    </button>

                    <button
                        className="test-field-btn"
                        onClick={async () => {
                            try {
                                setCompleteSystemRebuilding(true);
                                const response = await apiHelper.post('/api/qdrant/test-repository-indexing', {
                                    sessionId: 'test-field-' + Date.now(),
                                    testLimit: 3
                                });

                                if (response.data.success) {
                                    const successMessage = `🔍 DoNotShowInSources Field Test Results:

📊 Documents indexed: ${response.data.documentsIndexed}
Errors: ${response.data.errors}
🗂️ Collection: ${response.data.collection}

Check server console for detailed field debugging!

${response.data.successfulIndexes.length > 0 ?
                                        `✅ Successfully indexed:
${response.data.successfulIndexes.map(doc => `• ${doc.title} (${doc.year})`).join('\n')}` : ''}

${response.data.failedIndexes.length > 0 ?
                                        `Failed to index:
${response.data.failedIndexes.map(doc => `• ${doc.title}: ${doc.error}`).join('\n')}` : ''}

🔍 Check server console for DoNotShowInSources field debug output!`;

                                    toast.success(successMessage, { autoClose: 12000 });
                                } else {
                                    throw new Error(response.data.error || 'Field test failed');
                                }
                            } catch (error) {
                                console.error('Test field error:', error);
                                toast.error(`Field test failed: ${error.message}`);
                            } finally {
                                setCompleteSystemRebuilding(false);
                            }
                        }}
                        disabled={completeSystemRebuilding || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #6f42c1, #5a32a3)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '10px 16px',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {completeSystemRebuilding ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}🔍 Testing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faInfoCircle} />
                                {' '}🔍 Test DoNotShowInSources Field
                            </>
                        )}
                    </button>

                    {/* Document ID Input Field */}
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '5px', 
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            🎯 Debug Specific Document ID (optional):
                        </label>
                        <input
                            type="text"
                            value={debugDocumentId}
                            onChange={(e) => setDebugDocumentId(e.target.value)}
                            placeholder="Enter document ID (e.g., 4043) or leave empty for first 5"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    <button
                        className="debug-fields-btn"
                        onClick={async () => {
                            try {
                                setCompleteSystemRebuilding(true);
                                const requestBody = {
                                    sessionId: 'debug-fields-' + Date.now(),
                                    testLimit: 5
                                };
                                
                                // Add filterById if document ID is provided
                                if (debugDocumentId && debugDocumentId.trim()) {
                                    requestBody.filterById = debugDocumentId.trim();
                                }
                                
                                const response = await apiHelper.post('/api/qdrant/debug-sharepoint-fields', requestBody);

                                if (response.data.success) {
                                    const analysis = response.data.analysis;
                                    
                                    // Extract document IDs from the analysis
                                    const documentIds = response.data.analysis.documentIds || [];
                                    
                                    const successMessage = `🔍 SharePoint Fields Analysis Results:

📊 Total documents analyzed: ${analysis.totalDocuments}
🎯 Documents with DoNotShowInSources: ${analysis.documentsWithDoNotShowInSources}
📋 Document IDs found: ${documentIds.join(', ') || 'None found'}

DoNotShowInSources values found:
${Object.entries(analysis.doNotShowInSourcesValues).map(([value, count]) => `• ${value}: ${count} documents`).join('\n') || '• None found'}

📋 Available fields (${Object.keys(analysis.fieldSummary).length} total):
${Object.keys(analysis.fieldSummary).slice(0, 10).join(', ')}${Object.keys(analysis.fieldSummary).length > 10 ? '...' : ''}

📊 SharePoint Columns (${response.data.availableColumns ? response.data.availableColumns.length : 0} total):
${response.data.availableColumns ? response.data.availableColumns.slice(0, 5).map(col => `${col.displayName} (${col.name})`).join(', ') : 'Schema not available'}${response.data.availableColumns && response.data.availableColumns.length > 5 ? '...' : ''}

🔍 Check server console for detailed field output and schema analysis!`;

                                    toast.success(successMessage, { autoClose: 15000 });
                                } else {
                                    throw new Error(response.data.error || 'Fields debug failed');
                                }
                            } catch (error) {
                                console.error('Debug fields error:', error);
                                toast.error(`Fields debug failed: ${error.message}`);
                            } finally {
                                setCompleteSystemRebuilding(false);
                            }
                        }}
                        disabled={completeSystemRebuilding || qdrantStatus !== 'online'}
                        style={{
                            background: 'linear-gradient(135deg, #17a2b8, #138496)',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            padding: '10px 16px',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {completeSystemRebuilding ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                {' '}🔍 Analyzing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faClipboardList} />
                                {' '}🔍 Analyze SharePoint Fields Only
                            </>
                        )}
                    </button>
                </div>

                <div className="debug-info">
                    <div className="info-card">
                        <h4>🔍 What these debugging tools do:</h4>
                        <ul>
                            <li>🔍 <strong>Debug Repository Access:</strong> Tests both repository access approaches to identify which one works</li>
                            <li>🧪 <strong>Test Repository Indexing:</strong> Attempts to index 3-5 repository documents using the working approach</li>
                            <li>🔍 <strong>Test DoNotShowInSources Field:</strong> Specifically tests the new field and logs all SharePoint fields to server console</li>
                            <li>🔍 <strong>Analyze SharePoint Fields Only:</strong> Safe field analysis without indexing (no 404 errors) - shows all available fields</li>
                            <li>📋 Shows environment variables and their status</li>
                            <li>🔧 Provides recommendations for fixing the issue</li>
                            <li>📊 <strong>Server Console Logging:</strong> All tests log detailed field information to the server console for debugging</li>
                        </ul>
                    </div>
                </div>
            </div>


            {/* Logs Viewer Modal */}
            <QdrantLogsViewer
                isOpen={showLogsViewer}
                onClose={() => {
                    setShowLogsViewer(false);
                    setLogsAutoRefresh(false); // Reset auto-refresh when closing
                }}
                autoRefreshOnOpen={logsAutoRefresh}
            />

            {/* Collection Documents Viewer Modal */}
            <CollectionDocumentsViewer
                isOpen={showDocumentsViewer}
                onClose={closeDocumentsViewer}
                collection={selectedCollection}
            />

            {/* WG Documents Debug Modal */}
            {showWGDocsModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        maxWidth: '900px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '24px',
                            borderBottom: '2px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: 'linear-gradient(135deg, #e67e22, #d35400)',
                            color: 'white',
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '12px'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                📄 Working Group Documents Debug
                            </h3>
                            <button 
                                onClick={() => setShowWGDocsModal(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '24px' }}>
                            {isLoadingWGDocs ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#e67e22' }} />
                                    <p style={{ marginTop: '16px', color: '#666' }}>Checking Qdrant collection...</p>
                                </div>
                            ) : wgDocsResults ? (
                                wgDocsResults.success ? (
                                    <>
                                        {/* Summary */}
                                        <div style={{
                                            background: wgDocsResults.totalFound > 0 ? 'linear-gradient(135deg, #d4edda, #c3e6cb)' : 'linear-gradient(135deg, #f8d7da, #f5c6cb)',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            marginBottom: '24px',
                                            border: wgDocsResults.totalFound > 0 ? '2px solid #28a745' : '2px solid #dc3545'
                                        }}>
                                            <h4 style={{ margin: '0 0 12px 0', color: wgDocsResults.totalFound > 0 ? '#155724' : '#721c24' }}>
                                                {wgDocsResults.totalFound > 0 ? '✅ ' : '❌ '}
                                                Found {wgDocsResults.totalFound} Working Group Document{wgDocsResults.totalFound !== 1 ? 's' : ''}
                                            </h4>
                                            <p style={{ margin: '4px 0', color: wgDocsResults.totalFound > 0 ? '#155724' : '#721c24', fontSize: '14px' }}>
                                                <strong>Collection:</strong> {wgDocsResults.collection}
                                            </p>
                                        </div>

                                        {/* Documents List */}
                                        {wgDocsResults.totalFound > 0 ? (
                                            <div>
                                                <h4 style={{ marginBottom: '16px', color: '#333' }}>
                                                    📋 Indexed WG Documents ({wgDocsResults.documents.length}):
                                                </h4>
                                                <div style={{
                                                    maxHeight: '400px',
                                                    overflow: 'auto',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '8px'
                                                }}>
                                                    {wgDocsResults.documents.map((doc, index) => (
                                                        <div key={index} style={{
                                                            padding: '16px',
                                                            borderBottom: index < wgDocsResults.documents.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                            backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                                                        }}>
                                                            <div style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                                                                {doc.title || 'Untitled'}
                                                            </div>
                                                            <div style={{ fontSize: '13px', color: '#666', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                                <div><strong>WG:</strong> {doc.title || 'N/A'}</div>
                                                                <div><strong>Type:</strong> {doc.type || 'N/A'}</div>
                                                                <div><strong>Year:</strong> {doc.publishDate || 'N/A'}</div>
                                                                {/* <div><strong>Date:</strong> {doc.publishDate ? new Date(doc.publishDate).toLocaleDateString('en-GB') : 'N/A'}</div> */}
                                                            </div>
                                                            {doc.contentPreview && (
                                                                <div style={{
                                                                    marginTop: '8px',
                                                                    padding: '8px',
                                                                    background: '#f8f9fa',
                                                                    borderRadius: '4px',
                                                                    fontSize: '12px',
                                                                    color: '#666',
                                                                    fontFamily: 'monospace'
                                                                }}>
                                                                    {doc.contentPreview}...
                                                                </div>
                                                            )}
                                                        </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '40px',
                                                background: '#f8f9fa',
                                                borderRadius: '8px'
                                            }}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545' }} />
                                                <h4 style={{ marginTop: '16px', color: '#721c24' }}>⚠️ No Working Group Documents Found!</h4>
                                                <p style={{ color: '#721c24', marginTop: '8px' }}>
                                                    The regular WG documents (meeting records, minutes) are missing from the index. 
                                                    This may have been caused by the recent WG Updates indexing.
                                                </p>
                                                <p style={{ color: '#856404', marginTop: '12px', fontWeight: '600' }}>
                                                    Run a complete content rebuild to restore them.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        background: '#f8d7da',
                                        borderRadius: '8px',
                                        border: '2px solid #dc3545'
                                    }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545' }} />
                                        <h4 style={{ marginTop: '16px', color: '#721c24' }}>Error</h4>
                                        <p style={{ color: '#721c24', marginTop: '8px' }}>
                                            {wgDocsResults.error}
                                        </p>
                                    </div>
                                )
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* WG Updates Debug Modal */}
            {showWGDebugModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        maxWidth: '900px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '24px',
                            borderBottom: '2px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                            color: 'white',
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '12px'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                🔍 Working Group Updates Debug
                            </h3>
                            <button 
                                onClick={() => setShowWGDebugModal(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '24px' }}>
                            {isLoadingWGDebug ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#9b59b6' }} />
                                    <p style={{ marginTop: '16px', color: '#666' }}>Checking Qdrant collection...</p>
                                </div>
                            ) : wgDebugResults ? (
                                wgDebugResults.success ? (
                                    <>
                                        {/* Summary */}
                                        <div style={{
                                            background: wgDebugResults.totalFound > 0 ? 'linear-gradient(135deg, #d4edda, #c3e6cb)' : 'linear-gradient(135deg, #f8d7da, #f5c6cb)',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            marginBottom: '24px',
                                            border: wgDebugResults.totalFound > 0 ? '2px solid #28a745' : '2px solid #dc3545'
                                        }}>
                                            <h4 style={{ margin: '0 0 12px 0', color: wgDebugResults.totalFound > 0 ? '#155724' : '#721c24' }}>
                                                {wgDebugResults.totalFound > 0 ? '✅ ' : '❌ '}
                                                Found {wgDebugResults.totalFound} Working Group Update{wgDebugResults.totalFound !== 1 ? 's' : ''}
                                            </h4>
                                            <p style={{ margin: '4px 0', color: wgDebugResults.totalFound > 0 ? '#155724' : '#721c24', fontSize: '14px' }}>
                                                <strong>Collection:</strong> {wgDebugResults.collection}
                                            </p>
                                        </div>

                                        {/* Documents List */}
                                        {wgDebugResults.totalFound > 0 ? (
                                            <div>
                                                <h4 style={{ marginBottom: '16px', color: '#333' }}>
                                                    📋 Indexed WG Updates ({wgDebugResults.documents.length}):
                                                </h4>
                                                <div style={{
                                                    maxHeight: '400px',
                                                    overflow: 'auto',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '8px'
                                                }}>
                                                    {wgDebugResults.documents.map((doc, index) => (
                                                        <div key={index} style={{
                                                            padding: '16px',
                                                            borderBottom: index < wgDebugResults.documents.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                            backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                                                        }}>
                                                            <div style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                                                                {doc.title || 'Untitled'}
                                                            </div>
                                                            <div style={{ fontSize: '13px', color: '#666', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                                <div><strong>WG:</strong> {doc.workingGroup || 'N/A'}</div>
                                                                <div><strong>WG ID:</strong> {doc.workingGroupId || 'N/A'}</div>
                                                                <div><strong>Date:</strong> {doc.updateDate ? new Date(doc.updateDate).toLocaleDateString('en-GB') : 'N/A'}</div>
                                                                <div><strong>Year:</strong> {doc.year || 'N/A'}</div>
                                                            </div>
                                                            {doc.contentPreview && (
                                                                <div style={{
                                                                    marginTop: '8px',
                                                                    padding: '8px',
                                                                    background: '#f8f9fa',
                                                                    borderRadius: '4px',
                                                                    fontSize: '12px',
                                                                    color: '#666',
                                                                    fontFamily: 'monospace'
                                                                }}>
                                                                    {doc.contentPreview}...
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '40px',
                                                background: '#f8f9fa',
                                                borderRadius: '8px'
                                            }}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#ffc107' }} />
                                                <h4 style={{ marginTop: '16px', color: '#856404' }}>No Working Group Updates Found</h4>
                                                <p style={{ color: '#856404', marginTop: '8px' }}>
                                                    The WG Updates might not be indexed yet. Try clicking "Index WG Updates" in the Working Groups page.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        background: '#f8d7da',
                                        borderRadius: '8px',
                                        border: '2px solid #dc3545'
                                    }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545' }} />
                                        <h4 style={{ marginTop: '16px', color: '#721c24' }}>Error</h4>
                                        <p style={{ color: '#721c24', marginTop: '8px' }}>
                                            {wgDebugResults.error}
                                        </p>
                                    </div>
                                )
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IndexingManagement; 